/**
 * Strategy-generic signal labels for the unified crosshair tooltip (R131).
 *
 * Every chart strategy that emits `signalsByTime` gets its firing condition
 * rendered as a bold heading + a one-line PLAIN-ENGLISH description the user
 * can correlate with what they see on the chart — no internal jargon. When a
 * strategy defines multiple Buy/Sell/Short/Cover conditions, the condition
 * tag (`reason`) selects the matching description.
 *
 *   RockBand → strength-variant descriptions (delegates to rockBandLabels).
 *   Sniper   → B_C1…S_C3 / SELL / COVER condition descriptions (R130 rules).
 *   Others   → generic fallback wording so new strategies degrade gracefully.
 */
import { rockBandCondLabel } from './rockBandLabels';

export interface StrategySignalInputs {
  signal: 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT';
  strength: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  reason?: string | null;
}

export interface StrategySignalLabel {
  /** Bold heading, e.g. "BUY · B_C1" or "BUY · High". */
  heading: string;
  /** One-line plain-English description of why the signal fired. */
  detail: string;
  tone: 'bull' | 'bear' | 'flat';
}

/** Sniper R130 — what each condition means, in chart-visible terms. */
const SNIPER_REASON_DETAIL: Record<string, string> = {
  B_C1: 'Dip-buy: the higher timeframe is rising while this timeframe pulls back, and this candle closed right at its top (no upper wick) on a volume spike — buyers absorbed the dip.',
  B_C2: 'Dip-buy: the higher timeframe is rising while this timeframe pulls back; this candle left a lower rejection wick and the volume spike came on an up candle — the dip was bought hard.',
  B_C3: 'Trend continuation buy: both timeframes are rising and this candle opened at its bottom (no lower wick) with an up-candle volume spike — momentum pushing higher.',
  S_C1: 'Rip-sell: the higher timeframe is falling while this timeframe bounces, and this candle closed right at its bottom (no lower wick) on a volume spike — sellers capped the bounce.',
  S_C2: 'Rip-sell: the higher timeframe is falling while this timeframe bounces; this candle left an upper rejection wick and the volume spike came on a down candle — the bounce was sold hard.',
  S_C3: 'Trend continuation short: both timeframes are falling and this candle opened at its top (no upper wick) with a down-candle volume spike — momentum pushing lower.',
  SELL: 'Exit long: a fresh downtrend just started on this timeframe — the first lower close after the pullback that opened the trade ended.',
  COVER: 'Exit short: a fresh uptrend just started on this timeframe — the first higher close after the bounce that opened the trade ended.',
};

/** Om — the 4H swing rule. Described in what the chart SHOWS, not in internals:
 *  the "trend line" is the AV_Band slow HMA, the "stair" is the MM band. */
const OM_REASON_DETAIL: Record<string, string> = {
  OM_FLIP_UP: 'Buy the turn: the AV_Band slow line just turned up — price (as its 50-bar regression) crossed above it while the line itself started rising — and the daily trend agrees. Both timeframes turned together.',
  OM_FLIP_DOWN: 'Short the turn: the AV_Band slow line just turned down — the 50-bar regression fell below it while the line itself started falling — and the daily trend agrees.',
  OM_MM_STOP: 'Stopped out: price closed through the MM stair on this side of the trade. That stair only moves when a reversal or a volume spike prints, so it is a structural level, not a per-bar trail.',
  OM_TIME: 'Time exit: the trade has run its full 5 days. Measured on 6 years of 4H data, holding past this point gave money back rather than making more.',
  OM_REGIME: 'Trend flipped: the AV_Band slow line turned to the opposite direction outright. A merely neutral line does NOT close the trade — only a full reversal does.',
};

/** OM_Simple — one rule, described exactly as the chart shows it. There are no
 *  exit codes because the strategy has no exits: a BUY is closed by the next
 *  SHORT, and the stop and target are placed by hand. */
const OM_SIMPLE_REASON_DETAIL: Record<string, string> = {
  OMS_FLIP_UP: 'The CLN candle colour just turned from red to green — the regression of the closes crossed above the regression of the opens on this bar. Buy the turn. There is no automatic exit: this long is closed only by the next SHORT, so set your own stop and target.',
  OMS_FLIP_DOWN: 'The CLN candle colour just turned from green to red — the regression of the closes fell below the regression of the opens on this bar. Short the turn. There is no automatic exit: this short is closed only by the next BUY, so set your own stop and target.',
  CLNB_BREAK_UP: 'Price traded through the HIGHEST of the last few CLN regression body tops, so CLN_Break went long. This is the first position after the warm-up — from here it is always in the market, and only the lower level closes it.',
  CLNB_BREAK_DOWN: 'Price traded through the LOWEST of the last few CLN regression body bottoms, so CLN_Break went short. First position after the warm-up; only the upper level closes it.',
  CLNB_REVERSE_UP: 'The upper level was hit while short, so the short was squared off and a long opened at the same price. One order, twice the turnover — this rule pays a full round trip on every reversal.',
  CLNB_REVERSE_DOWN: 'The lower level was hit while long, so the long was squared off and a short opened at the same price. One order, twice the turnover — this rule pays a full round trip on every reversal.',
  LRX_CROSS_UP: 'The source candle (Regular or Heikin-Ashi, per the LR_Cross setting) closed ABOVE the top of its own LR body for the first time. If a short was open this also exits it (B ✕S). No stop or target is set for you; an option idea for this side is a Bull Put (credit).',
  LRX_CROSS_DOWN: 'The source candle closed BELOW the bottom of its own LR body for the first time. If a long was open this also exits it (S ✕L). No stop or target is set for you; an option idea for this side is a Bear Call (credit).',
  CLNB_STOP: 'The ATR stop was hit, so the position was closed and CLN_Break is flat until a level is breached again. The rule was measured WITHOUT a stop; switching one on is a risk choice, not a returns one.',
};

const GANDIV_REASON_DETAIL: Record<string, string> = {
  GANDIV_BAND_UP: "The source close crossed ABOVE the higher-timeframe band, so Gandiv went long. The stop is the band's LOWER line at this bar, fixed for the life of the trade.",
  GANDIV_BAND_DOWN: "The source close crossed BELOW the higher-timeframe band, so Gandiv went short. The stop is the band's UPPER line at this bar, fixed for the life of the trade.",
  GANDIV_SL_REV_UP: "The short's stop was crossed, so it was closed and a long opened at this close. The new stop is the band's LOWER line from when the stopped short was opened.",
  GANDIV_SL_REV_DOWN: "The long's stop was crossed, so it was closed and a short opened at this close. The new stop is the band's UPPER line from when the stopped long was opened.",
  GANDIV_BAND_REV_UP: "While short, the source close crossed ABOVE the band before the stop was reached. The short was closed and a long opened, with a fresh stop at the band's lower line.",
  GANDIV_BAND_REV_DOWN: "While long, the source close crossed BELOW the band before the stop was reached. The long was closed and a short opened, with a fresh stop at the band's upper line.",
  GANDIV_SL_FLAT: 'The stop was crossed, but no stop for the reverse trade was on the right side of the price, so Gandiv went flat and waits for the next band cross.',
};

export function describeStrategySignal(
  strategyId: string | undefined,
  sig: StrategySignalInputs,
): StrategySignalLabel {
  const isExit = sig.signal === 'EXIT_LONG' || sig.signal === 'EXIT_SHORT';

  if (strategyId === 'om') {
    const detail = OM_REASON_DETAIL[(sig.reason || '').toUpperCase()];
    if (detail) {
      const side = sig.signal === 'BUY' ? 'BUY' : sig.signal === 'SHORT' ? 'SHORT'
        : sig.signal === 'EXIT_LONG' ? 'SELL' : 'COVER';
      const tone: StrategySignalLabel['tone'] = isExit ? 'flat' : sig.signal === 'BUY' ? 'bull' : 'bear';
      return { heading: side, detail, tone };
    }
  }

  // CLN_Break's reasons live in the same map; until 2026-09-24 only om_simple
  // looked them up, so every CLN_Break tooltip fell through to the generic text.
  if (strategyId === 'om_simple' || strategyId === 'clnbreak' || strategyId === 'lr_cross') {
    const detail = OM_SIMPLE_REASON_DETAIL[(sig.reason || '').toUpperCase()];
    if (detail) {
      return {
        heading: isExit ? 'FLAT' : sig.signal === 'BUY' ? 'BUY' : 'SHORT',
        detail,
        tone: isExit ? 'flat' : sig.signal === 'BUY' ? 'bull' : 'bear',
      };
    }
  }

  if (strategyId === 'gandiv') {
    const detail = GANDIV_REASON_DETAIL[(sig.reason || '').toUpperCase()];
    if (detail) {
      const flat = sig.signal === 'EXIT_LONG' || sig.signal === 'EXIT_SHORT';
      return {
        heading: flat ? 'FLAT' : sig.signal === 'BUY' ? 'BUY' : 'SHORT',
        detail,
        tone: flat ? 'flat' : sig.signal === 'BUY' ? 'bull' : 'bear',
      };
    }
  }

  if (strategyId === 'rockband') {
    const l = rockBandCondLabel({ signal: sig.signal, strength: sig.strength });
    return { heading: l.heading, detail: l.detail, tone: l.tone };
  }

  if (strategyId === 'sniper') {
    const reason = (sig.reason || '').toUpperCase();
    const detail = SNIPER_REASON_DETAIL[reason];
    if (detail) {
      const side = sig.signal === 'BUY' ? 'BUY' : sig.signal === 'SHORT' ? 'SHORT'
        : sig.signal === 'EXIT_LONG' ? 'SELL' : 'COVER';
      const heading = isExit ? side : `${side} · ${reason}`;
      const tone: StrategySignalLabel['tone'] = isExit ? 'flat' : sig.signal === 'BUY' ? 'bull' : 'bear';
      return { heading, detail, tone };
    }
  }

  if (strategyId === 'dailyblast' && (sig.signal === 'BUY' || sig.signal === 'SHORT')) {
    const s = sig.strength ? ` · ${sig.strength.charAt(0)}${sig.strength.slice(1).toLowerCase()}` : '';
    return {
      heading: `${sig.signal}${s}`,
      detail: sig.signal === 'BUY'
        ? 'Heikin-Ashi broke above the prior 2-bar high after a prepare-long state (light-green following dark-red).'
        : 'Heikin-Ashi broke below the prior 2-bar low after a prepare-short state (light-red following dark-green).',
      tone: sig.signal === 'BUY' ? 'bull' : 'bear',
    };
  }

  if (strategyId === 'shaved_doji' && (sig.signal === 'BUY' || sig.signal === 'SHORT')) {
    return {
      heading: sig.signal,
      detail: sig.signal === 'BUY'
        ? 'CLH close rose above the previous CLH close — flip to long (covers any short).'
        : 'CLH close fell below the previous CLH close — flip to short (sells any long).',
      tone: sig.signal === 'BUY' ? 'bull' : 'bear',
    };
  }

  // Generic fallback — any strategy without bespoke wording.
  if (sig.signal === 'EXIT_LONG')  return { heading: 'SELL',  detail: 'Strategy exit — close the long position.', tone: 'flat' };
  if (sig.signal === 'EXIT_SHORT') return { heading: 'COVER', detail: 'Strategy exit — close the short position.', tone: 'flat' };
  const tone: StrategySignalLabel['tone'] = sig.signal === 'BUY' ? 'bull' : 'bear';
  return {
    heading: sig.reason ? `${sig.signal} · ${sig.reason}` : sig.signal,
    detail: `Strategy ${sig.signal === 'BUY' ? 'long' : 'short'} entry signal on this candle.`,
    tone,
  };
}
